import {
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Flex,
  SimpleGrid,
  Box,
  Checkbox,
  Anchor,
} from "@mantine/core";
import { IconBuilding, IconMail, IconLock, IconBriefcase } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import VerifyEmailModal from "../signup/VerifyModal";
import trpc from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util";
import { successNotification } from "../../shared/utils/successNotification";
import { SignupFlowLayout } from "./SignupFlowLayout";
import "react-international-phone/style.css";
import { PhoneInput } from "react-international-phone";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

const TERMS_URL = "https://gtsetu.com/terms/";
const PRIVACY_URL = "https://gtsetu.com/privacy/";

function SignupPage() {
  const navigate = useNavigate();

  const [emailModalOpened, setEmailModalOpened] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = Cookies.get("token");

    if (!token) return;

    try {
      const decoded = jwtDecode<{ role: string; exp: number }>(token);

      if (decoded.exp > Date.now() / 1000) {
        if (decoded.role === "ADMIN") {
          navigate("/admin/companies", { replace: true });
        } else {
          navigate("/profile", { replace: true });
        }
      } else {
        Cookies.remove("token");
      }
    } catch {
      Cookies.remove("token");
    }
  }, []);

  const [formData, setFormData] = useState({
    companyName: "",
    name: "",
    designation: "",
    email: "",
    phoneNumber: "",
    phoneCountryCode: "",
    password: "",
    confirmPassword: "",
    acceptedTermsAndPrivacy: false,
  });

  const PERSONAL_EMAIL_DOMAINS = [
    "gmail.com",
    "yahoo.com",
    "yahoo.in",
    "yahoo.co.uk",
    "icloud.com",
    "me.com",
    "mac.com",
    "hotmail.com",
    "hotmail.in",
    "hotmail.co.uk",
    "outlook.com",
    "outlook.in",
    "live.com",
    "live.in",
    "msn.com",
    "aol.com",
    "protonmail.com",
    "proton.me",
    "zoho.com",
    "yandex.com",
    "yandex.ru",
    "mail.com",
    "gmx.com",
    "gmx.net",
    "rediffmail.com",
    "inbox.com",
    "fastmail.com",
    "tutanota.com",
  ];
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isBusinessEmail = (email: string) => {
    const domain = email.split("@")[1]?.toLowerCase();
    return domain ? !PERSONAL_EMAIL_DOMAINS.includes(domain) : true;
  };
  const isPasswordValid = formData.password.length >= 8;
  const doPasswordsMatch = formData.confirmPassword === "" || formData.password === formData.confirmPassword;

  const isStep1Valid =
    formData.name.trim() !== "" &&
    formData.companyName.trim() !== "" &&
    formData.designation.trim() !== "" &&
    formData.phoneNumber.trim() !== "" &&
    isValidEmail(formData.email) &&
    isBusinessEmail(formData.email) &&
    isPasswordValid &&
    formData.password === formData.confirmPassword &&
    formData.acceptedTermsAndPrivacy;

  const handleStep1Submit = async () => {
    if (!isStep1Valid) return;

    setLoading(true);
    try {
      await trpc.auth.registerSignupStep1.mutate({
        email: formData.email,
        password: formData.password,
        userName: formData.name,
        companyName: formData.companyName,
        userDesignation: formData.designation,
        userPhoneNumber: `${formData.phoneCountryCode}${formData.phoneNumber}`,
        acceptedTermsAndPrivacy: true,
      });

      successNotification({
        title: "Check your email",
        message: "We sent a verification link. After verifying, sign in to build your company profile and receive signup credits.",
      });

      navigate(`/resend-email?email=${encodeURIComponent(formData.email)}`);
      setEmailModalOpened(true);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleModalContinue = () => {
    setEmailModalOpened(false);
    navigate("/registration-success", {
      state: {
        companyName: formData.companyName,
        location: "—",
        email: formData.email,
      },
    });
  };

  return (
    <SignupFlowLayout>
      <Paper p="xl" radius="lg" maw={980} mx="auto" bg="#f7f9ff" style={{ border: "1px solid #04305c" }}>

        <Title order={2} mb="md" c="slate.7" ta="center" style={{ borderBottom: "1px solid #eee", paddingBottom: "8px" }}>
          Create your account
        </Title>
        <Text fz="lg" c="slate.6" mb="lg">
          Personal Details
        </Text>

        <TextInput
          label="Contact Person Name"
          placeholder="Enter name"
          required
          size="md"
          mb="md"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.currentTarget.value })}
        />

        <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
          <TextInput
            label="Company Name"
            placeholder="Enter your company name"
            required
            size="md"
            value={formData.companyName}
            onChange={(e) => setFormData({ ...formData, companyName: e.currentTarget.value })}
            leftSection={<IconBuilding size={18} />}
          />
          <TextInput
            label="Designation"
            placeholder="Enter designation"
            size="md"
            value={formData.designation}
            onChange={(e) => setFormData({ ...formData, designation: e.currentTarget.value })}
            leftSection={<IconBriefcase size={18} />}
          />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
          <TextInput
            label="Company Email Address"
            required
            type="email"
            placeholder="Enter email"
            size="md"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.currentTarget.value })}
            leftSection={<IconMail size={18} />}
            error={
              formData.email && !isValidEmail(formData.email)
                ? "Please enter a valid email address"
                : formData.email && isValidEmail(formData.email) && !isBusinessEmail(formData.email)
                  ? "Personal email addresses are not allowed. Please use your official business email."
                  : null
            }
          />
          <Box>
            <Text fz="sm" fw={500} mb={3}>
              Phone Number{" "}
              <Text span c="red">
                *
              </Text>
            </Text>
            <PhoneInput
              defaultCountry="in"
              value={`${formData.phoneCountryCode}${formData.phoneNumber}`}
              onChange={(phone, meta) => {
                const countryCode = meta.country?.dialCode ? `+${meta.country.dialCode}` : "";
                const nationalNumber = phone.replace(`+${meta.country?.dialCode || ""}`, "").trim();
                setFormData((prev) => ({ ...prev, phoneCountryCode: countryCode, phoneNumber: nationalNumber }));
              }}
              style={{ width: "100%" }}
              inputStyle={{
                width: "100%",
                height: "36px",
                fontSize: "14px",
                border: "1px solid var(--mantine-color-gray-3)",
                borderLeft: "none",
                borderRadius: "0 var(--mantine-radius-md) var(--mantine-radius-md) 0",
                outline: "none",
              }}
              countrySelectorStyleProps={{
                buttonStyle: {
                  height: "36px",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: "var(--mantine-radius-md) 0 0 var(--mantine-radius-md)",
                  padding: "0 12px",
                  backgroundColor: "white",
                },
                dropdownStyleProps: {
                  style: {
                    backgroundColor: "white",
                    boxShadow: "0 4px 12px #00000026",
                    border: "1px solid var(--mantine-color-gray-3)",
                    zIndex: 1000,
                  },
                },
              }}
            />
          </Box>
        </SimpleGrid>

        <Flex direction={{ base: "column", sm: "row" }} gap="md" mb="xl">
          <PasswordInput
            style={{ flex: 1 }}
            label="Password"
            placeholder="Enter password"
            required
            size="md"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.currentTarget.value })}
            visible={showPassword}
            onVisibilityChange={setShowPassword}
            leftSection={<IconLock size={18} />}
            error={formData.password.length > 0 && !isPasswordValid ? "Password must be at least 8 characters" : null}
          />

          <PasswordInput
            style={{ flex: 1 }}
            label="Confirm Password"
            placeholder="Re-enter password"
            required
            size="md"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.currentTarget.value })}
            visible={showConfirmPassword}
            onVisibilityChange={setShowConfirmPassword}
            leftSection={<IconLock size={18} />}
            error={formData.confirmPassword && !doPasswordsMatch ? "Passwords don't match" : null}
          />
        </Flex>

        <Checkbox
          size="xs"
          mb="lg"
          checked={formData.acceptedTermsAndPrivacy}
          onChange={(e) => setFormData({ ...formData, acceptedTermsAndPrivacy: e.currentTarget.checked })}
          label={
            <Text component="span" fz="xs" c="gray.6" lh={1.45}>
              By creating an account, I accept the{" "}
              <Anchor
                href={TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                c="gray.6"
                fw={500}
                fz="inherit"
                underline="always"
              >
                Terms & Conditions
              </Anchor>{" "}
              and{" "}
              <Anchor
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                c="gray.6"
                fw={500}
                fz="inherit"
                underline="always"
              >
                Privacy Policy
              </Anchor>
              .
            </Text>
          }
        />

        <Flex justify="flex-end">
          <Button
            size="lg"
            px="xl"
            onClick={handleStep1Submit}
            loading={loading}
            disabled={!isStep1Valid || loading}
            loaderProps={{ color: "white" }}
            styles={{
              root: {
                opacity: 1,
                ...(isStep1Valid
                  ? {
                    backgroundColor: "#04305c",
                    color: "#ffffff",
                    border: "1px solid #04305c",
                    "&:hover": loading
                      ? undefined
                      : {
                        backgroundColor: "#062e52",
                      },
                    "&:disabled": {
                      opacity: 1,
                      backgroundColor: "#04305c",
                      color: "#ffffff",
                      borderColor: "#04305c",
                    },
                  }
                  : {
                    backgroundColor: "#ffffff",
                    color: "var(--mantine-color-gray-5)",
                    border: "1px solid var(--mantine-color-gray-3)",
                    "&:disabled": {
                      opacity: 1,
                      backgroundColor: "#ffffff",
                      color: "var(--mantine-color-gray-5)",
                      borderColor: "var(--mantine-color-gray-3)",
                    },
                  }),
              },
            }}
          >
            Continue
          </Button>
        </Flex>
      </Paper>

      <VerifyEmailModal
        opened={emailModalOpened}
        onClose={() => setEmailModalOpened(false)}
        email={formData.email}
        onContinue={handleModalContinue}
      />
    </SignupFlowLayout>
  );
}

export default SignupPage;
